<script lang="ts">
	import {
		ProjectViewDialog,
		type OutputObjectPromptVariantsRequest,
		type OutputObjectPromptVariantsResponse
	} from '@repo/commons/models'
	import { Button, ButtonSize, ButtonType, Chip, MarkdownInput } from '@repo/commons/components'
	import {
		getConstraintNamesFromKey,
		OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG
	} from '@repo/commons/utils'
	import {
		type SupportedLanguage as SupportedLanguage,
		Modality,
		type Constraint
	} from '@semanticedge/persistence-service-noheaders-client/src'

	type Prompts = Record<string, Record<Modality, Record<SupportedLanguage, string[]>>>

	interface Props {
		dialogRequest: OutputObjectPromptVariantsRequest
		constraints: Constraint[]
		onDialogResponse: (response: OutputObjectPromptVariantsResponse) => void
		onClose?: () => void
	}

	let { dialogRequest, constraints, onDialogResponse, onClose }: Props = $props()

	const language = dialogRequest.language as SupportedLanguage
	const selectedModality: Modality = dialogRequest.selectedModality
	const prompts = dialogRequest.prompts as Prompts

	// Ensure that prompts exists
	if (!prompts) {
		dialogRequest.prompts = {} as typeof dialogRequest.prompts
	}
	// For each key in prompts, ensure there is a bucket for this modality and language.
	for (const key in prompts) {
		if (!prompts[key][selectedModality]) {
			prompts[key][selectedModality] = {} as Record<SupportedLanguage, string[]>
		}
		if (!prompts[key][selectedModality][language]) {
			prompts[key][selectedModality][language] = ['']
		}
	}

	// Reactive update: when input changes, reassign the array so Svelte picks up the change.
	const handleInput = (key: string, index: number, value: string) => {
		if (!prompts[key][selectedModality]) {
			prompts[key][selectedModality] = {} as Record<SupportedLanguage, string[]>
		}
		if (!prompts[key][selectedModality][language]) {
			prompts[key][selectedModality][language] = []
		}
		const arr = prompts[key][selectedModality][language]
		arr[index] = value
		prompts[key][selectedModality][language] = arr
	}

	const handleBlur = (key: string, index: number) => {
		// Optional: mark field as touched, etc.
	}

	// Reassign the array with a new empty variant to trigger reactivity.
	const handleAddVariant = (key: string) => {
		if (!prompts[key][selectedModality]) {
			prompts[key][selectedModality] = {} as Record<SupportedLanguage, string[]>
		}
		if (!prompts[key][selectedModality][language]) {
			prompts[key][selectedModality][language] = []
		}
		prompts[key][selectedModality][language] = [...prompts[key][selectedModality][language], '']
	}

	// Before dispatching the response, filter out any empty variant strings.
	const handleSubmit = () => {
		for (const key in prompts) {
			if (prompts[key][selectedModality] && prompts[key][selectedModality][language]) {
				prompts[key][selectedModality][language] = prompts[key][selectedModality][language].filter(
					(variant: string) => variant.trim() !== ''
				)
			}
		}
		onDialogResponse({
			prompts: dialogRequest.prompts,
			selectedConstraintIds: dialogRequest.selectedConstraintIds,
			language: dialogRequest.language,
			selectedModality: dialogRequest.selectedModality,
			dialogType: ProjectViewDialog.OUTPUT_OBJECT
		} as OutputObjectPromptVariantsResponse)
	}

	// Delete a variant at a given index; only allow deletion if index > 0 (the first variant is Default).
	const handlePromptDeleted = (key: string, index: number) => {
		if (index > 0) {
			let arr = prompts[key][selectedModality][language]
			arr = arr.filter((_: string, i: number) => i !== index)
			prompts[key][selectedModality][language] = arr
		}
	}

	const getVariantTitle = (index: number) => {
		return index === 0 ? 'Default' : `Variant ${index}`
	}
</script>

<div class="overall-container">
	<div class="dialog-content">
		{#if prompts && prompts[dialogRequest.selectedConstraintIds] && prompts[dialogRequest.selectedConstraintIds][selectedModality]}
			<div class="flex flex-col gap-8 mb-4">
				{#each prompts[dialogRequest.selectedConstraintIds][selectedModality][language] as variant, index (index)}
					<div>
						<div class="flex justify-between items-center gap-2">
							<p class="text-base">{getVariantTitle(index)}</p>
							<Button
								buttonType={ButtonType.TERTIARY_INVERSE}
								buttonSize={ButtonSize.EXTRA_SMALL}
								rounded
								disabled={index === 0}
								iconProps={{ name: 'trash', size: 'sm', class: 'text-[#002C4D80]' }}
								onclick={() => handlePromptDeleted(dialogRequest.selectedConstraintIds, index)}
							/>
						</div>
						{#if selectedModality === Modality.Voice}
							{@const chips =
								getConstraintNamesFromKey(constraints, dialogRequest.selectedConstraintIds) || []}
							<MarkdownInput
								toolbarConfig={OUTPUT_OBJECT_VOICE_PROMPT_TOOLBAR_CONFIG}
								value={variant || ''}
								onInput={(value: string) =>
									handleInput(dialogRequest.selectedConstraintIds, index, value)}
								onBlur={() => handleBlur(dialogRequest.selectedConstraintIds, index)}
							>
								<div class="bottom-row">
									{#if chips}
										<div class="chips-container">
											{#each chips as chip}
												<Chip text={chip} removable={false} />
											{/each}
										</div>
									{/if}
								</div>
							</MarkdownInput>
						{:else}
							<MarkdownInput
								value={variant || ''}
								enableImageUpload={true}
								onInput={(value: string) =>
									handleInput(dialogRequest.selectedConstraintIds, index, value)}
								onBlur={() => handleBlur(dialogRequest.selectedConstraintIds, index)}
							/>
						{/if}
					</div>
				{/each}
				<div class="sidesheet-form-element">
					<Button
						buttonType={ButtonType.NEUTRAL}
						buttonText="Add New Variant"
						fillWidth={true}
						onclick={() => handleAddVariant(dialogRequest.selectedConstraintIds)}
					/>
				</div>
			</div>
		{/if}
	</div>

	<div class="submit-container">
		<Button
			buttonType={ButtonType.BRAND}
			buttonSize={ButtonSize.LARGE}
			onclick={handleSubmit}
			buttonText="Submit"
		/>
		<Button
			buttonText="Cancel"
			buttonType={ButtonType.TERTIARY_INVERSE}
			buttonSize={ButtonSize.LARGE}
			onclick={onClose}
		/>
	</div>
</div>

<style lang="postcss">
	.overall-container {
		@apply w-full mx-auto;
	}
	.submit-container {
		@apply mt-4 flex gap-4;
	}
	.bottom-row {
		@apply flex justify-between items-start border-t border-[#002C4D80]/20 bg-gray-50 px-2 py-4;
	}

	.chips-container {
		@apply flex flex-wrap gap-2;
		max-width: 70%;
	}
</style>
